import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import AppHeader from "@/components/AppHeader";
import { calculateMolarMass } from "@/data/molarMass";
import Main from "@/components/Main";
import WorkedExample from "@/components/WorkedExample";
import Seo from "@/components/Seo";

const examples = ["H2O", "NaCl", "Ca(OH)2", "H2SO4", "C6H12O6", "Al2(SO4)3", "Fe2O3", "CH3COOH"];

export default function MolarMassCalculator() {
  const [input, setInput] = useState("");
  const [result, setResult] = useState<ReturnType<typeof calculateMolarMass> | null>(null);
  const [searchParams] = useSearchParams();

  // Pre-fill from ?formula= for deep-links from other tools (e.g. Lab).
  useEffect(() => {
    const formula = searchParams.get("formula");
    if (formula) {
      setInput(formula);
      setResult(calculateMolarMass(formula));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleCalculate = () => {
    if (!input.trim()) return;
    setResult(calculateMolarMass(input));
  };

  return (
    <div className="min-h-screen bg-transparent">
      <Seo title="Molar Mass Calculator — ElementPad" description="Calculate molar mass for any chemical formula. Free online molecular weight calculator with element-by-element breakdown." path="/tools/molar-mass" jsonLd={{"@context":"https://schema.org","@type":"SoftwareApplication","name":"Molar Mass Calculator","applicationCategory":"EducationalApplication","operatingSystem":"Web","offers":{"@type":"Offer","price":"0","priceCurrency":"USD"}}} />
      <AppHeader />
      <Main className="max-w-3xl mx-auto px-4 py-8">
        <h1 className="text-2xl font-bold text-foreground mb-1">Molar Mass Calculator</h1>
        <p className="text-sm text-muted-foreground mb-6">
          Enter a chemical formula to calculate its molar mass with element-by-element breakdown.
        </p>

        {/* Input */}
        <div className="flex gap-2 mb-4">
          <label htmlFor="formula-input" className="sr-only">Chemical formula</label>
          <input
            id="formula-input"
            type="text"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && handleCalculate()}
            placeholder="e.g. Ca(OH)2"
            className="flex-1 h-11 rounded-lg border border-input bg-tile-bg px-4 font-mono text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring"
          />
          <button
            onClick={handleCalculate}
            className="h-11 px-6 rounded-lg bg-primary text-primary-foreground font-medium hover:bg-primary/90 transition-colors"
          >
            Calculate
          </button>
        </div>

        {/* Examples */}
        <div className="flex flex-wrap gap-2 mb-8">
          <span className="text-xs text-muted-foreground pt-1">Try:</span>
          {examples.map((ex) => (
            <button
              key={ex}
              onClick={() => {
                setInput(ex);
                setResult(calculateMolarMass(ex));
              }}
              className="text-xs px-2.5 py-1 rounded-md bg-muted text-muted-foreground hover:text-foreground hover:bg-accent transition-colors font-mono"
            >
              {ex}
            </button>
          ))}
        </div>

        {/* Result */}
        {result && (
          <div className="space-y-4">
            {result.error ? (
              <div className="rounded-lg border border-destructive/30 bg-destructive/5 p-4">
                <p className="text-sm text-destructive">{result.error}</p>
              </div>
            ) : (
              <>
                {/* Total */}
                <div className="rounded-lg border border-primary/30 bg-primary/5 p-4">
                  <p className="text-xs text-muted-foreground uppercase tracking-wider mb-1">Molar Mass</p>
                  <p className="font-mono text-2xl font-bold text-foreground">
                    {result.total.toFixed(3)} <span className="text-base font-normal text-muted-foreground">g/mol</span>
                  </p>
                </div>

                {/* Breakdown table */}
                <div className="rounded-lg border border-border bg-tile-bg overflow-hidden">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-border">
                        <th className="text-left p-3 text-xs text-muted-foreground font-medium uppercase tracking-wider">Element</th>
                        <th className="text-right p-3 text-xs text-muted-foreground font-medium uppercase tracking-wider">Count</th>
                        <th className="text-right p-3 text-xs text-muted-foreground font-medium uppercase tracking-wider">Atomic Mass</th>
                        <th className="text-right p-3 text-xs text-muted-foreground font-medium uppercase tracking-wider">Subtotal</th>
                        <th className="text-right p-3 text-xs text-muted-foreground font-medium uppercase tracking-wider">%</th>
                      </tr>
                    </thead>
                    <tbody>
                      {result.breakdown.map((item) => (
                        <tr key={item.symbol} className="border-b border-border last:border-0">
                          <td className="p-3 font-mono font-semibold text-foreground">{item.symbol}</td>
                          <td className="p-3 text-right font-mono text-foreground">{item.count}</td>
                          <td className="p-3 text-right font-mono text-muted-foreground">{item.mass.toFixed(3)}</td>
                          <td className="p-3 text-right font-mono text-foreground">{item.totalMass.toFixed(3)}</td>
                          <td className="p-3 text-right">
                            <div className="flex items-center justify-end gap-2">
                              <div className="w-16 h-1.5 rounded-full bg-muted overflow-hidden">
                                <div
                                  className="h-full rounded-full bg-primary"
                                  style={{ width: `${item.percentage}%` }}
                                />
                              </div>
                              <span className="font-mono text-muted-foreground min-w-[3rem] text-right">
                                {item.percentage.toFixed(1)}%
                              </span>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </div>
        )}

        {/* Worked example */}
        <div className="mt-8">
          <WorkedExample
            title="Molar mass of Ca(OH)₂"
            problem="Calculate the molar mass of calcium hydroxide, Ca(OH)₂."
            steps={[
              {
                label: "Step 1: Identify each element and how many atoms",
                explain:
                  "The subscript 2 outside the parentheses applies to BOTH O and H - a common slip-up.",
                formula: "Ca: 1   O: 2   H: 2",
              },
              {
                label: "Step 2: Look up atomic masses (g/mol)",
                explain: "Use the periodic table values to 2 decimal places.",
                formula: "Ca = 40.08   O = 16.00   H = 1.01",
              },
              {
                label: "Step 3: Multiply atoms × atomic mass for each element",
                explain: "Keep units (g/mol) attached.",
                formula:
                  "Ca: 1 × 40.08 = 40.08\nO:  2 × 16.00 = 32.00\nH:  2 × 1.01  = 2.02",
              },
              {
                label: "Step 4: Add them together",
                explain: "This is the formula mass - same number, different name in some textbooks.",
                formula: "40.08 + 32.00 + 2.02 = 74.10 g/mol",
              },
            ]}
            answer="Molar mass of Ca(OH)₂ = 74.10 g/mol"
          />
        </div>
      </Main>
    </div>
  );
}
